'use client'

import { useState, type FormEvent } from 'react'
import { useRouter } from 'next/navigation'
import {
  FieldGrid,
  FieldGroup,
  FieldLabel,
  LabelWithTooltip,
  PrimaryButton,
  SelectField,
  TextareaField,
} from '@/app/FormFields'

export default function ExpertResponseForm({
  ticketId,
  initialDraft,
  initialSummary,
  severityOptions,
  concernOptions,
}: {
  ticketId: string
  initialDraft: string
  initialSummary: string | null
  severityOptions: string[]
  concernOptions: string[]
}) {
  const router = useRouter()
  const [responseText, setResponseText] = useState(initialDraft)
  const [severity, setSeverity] = useState('')
  const [concerns, setConcerns] = useState('')
  const [resolutionSummary, setResolutionSummary] = useState(initialSummary ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = responseText.trim().length >= 10 && severity !== '' && concerns !== ''

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    setError(null)

    const response = await fetch(`/api/tickets/${ticketId}/respond`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        responseText,
        resolutionSeverity: severity,
        resolutionConcerns: concerns,
        resolutionSummary: resolutionSummary.trim() || null,
      }),
    })

    setLoading(false)
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as { error?: string }
      setError(payload.error ?? 'Unable to send response')
      return
    }

    router.refresh()
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900 space-y-4"
    >
      <FieldGroup>
        <FieldLabel htmlFor="expert-response" required>Expert Response</FieldLabel>
        <TextareaField
          id="expert-response"
          value={responseText}
          onChange={(event) => setResponseText(event.target.value)}
          className="min-h-[220px]"
        />
      </FieldGroup>

      <FieldGrid>
        <FieldGroup>
          <LabelWithTooltip
            htmlFor="resolution-severity"
            required
            tooltipId="resolution-severity-help"
            tooltipText="Classify the operational impact of the issue after review. This metadata helps reporting and future knowledge-base recommendations."
          >
            Severity
          </LabelWithTooltip>
          <SelectField
            id="resolution-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            placeholderValue=""
            aria-describedby="resolution-severity-help"
          >
            <option value="">Select severity…</option>
            {severityOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </SelectField>
        </FieldGroup>
        <FieldGroup>
          <LabelWithTooltip
            htmlFor="resolution-concerns"
            required
            tooltipId="resolution-concerns-help"
            tooltipText="Capture the main reason this issue needed expert support, such as a process gap, knowledge gap, or unclear guidance."
          >
            Concern type
          </LabelWithTooltip>
          <SelectField
            id="resolution-concerns"
            value={concerns}
            onChange={(e) => setConcerns(e.target.value)}
            placeholderValue=""
            aria-describedby="resolution-concerns-help"
          >
            <option value="">Select concern…</option>
            {concernOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </SelectField>
        </FieldGroup>
      </FieldGrid>

      <FieldGroup>
        <LabelWithTooltip
          htmlFor="resolution-summary"
          tooltipId="resolution-summary-help"
          tooltipText="Use this to capture the durable lesson from the response. If left blank, the system uses the AI-generated summary."
        >
          Resolution summary <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">(optional — leave blank to use AI summary)</span>
        </LabelWithTooltip>
        <TextareaField
          id="resolution-summary"
          value={resolutionSummary}
          onChange={(e) => setResolutionSummary(e.target.value)}
          placeholder="Summarise the key facts of this resolution in your own words…"
          className="min-h-24"
          aria-describedby="resolution-summary-help"
        />
      </FieldGroup>

      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}

      <PrimaryButton
        type="submit"
        disabled={loading || !canSubmit}
      >
        {loading ? 'Sending...' : 'Send Response'}
      </PrimaryButton>
    </form>
  )
}
